/**
 * Enhanced Feature Flag System
 *
 * Supports:
 * - Admin-controlled flags (environment variables)
 * - User preferences (localStorage + database)
 * - A/B testing (automatic user assignment)
 * - Gradual rollouts (percentage-based)
 * - Redesign toggles (old vs new UI)
 */

import {
  useState,
  useEffect,
  createContext,
  useContext,
  ReactNode,
} from "react";

// Feature flag configuration
export interface FeatureFlag {
  key: string;
  name: string;
  description: string;
  type: "admin" | "user-opt-in" | "ab-test" | "rollout" | "redesign";
  defaultEnabled: boolean;
  adminOverride?: boolean; // Admin can force on/off
  rolloutPercentage?: number; // For gradual rollouts (0-100)
  requiresPremium?: boolean; // Only for premium users
  betaOnly?: boolean; // Only for beta testers
}

// Define all feature flags
export const FEATURE_FLAGS: Record<string, FeatureFlag> = {
  // Redesign flags - toggle between old and new UI
  NEW_NAVIGATION: {
    key: "NEW_NAVIGATION",
    name: "New Navigation Design",
    description: "Redesigned navigation with improved UX",
    type: "redesign",
    defaultEnabled: false,
    adminOverride: import.meta.env.VITE_FORCE_NEW_NAV === "true",
  },

  NEW_DASHBOARD: {
    key: "NEW_DASHBOARD",
    name: "New Dashboard Layout",
    description: "Modernized dashboard with better data visualization",
    type: "redesign",
    defaultEnabled: false,
    adminOverride: import.meta.env.VITE_FORCE_NEW_DASHBOARD === "true",
  },

  NEW_SKILL_CARD_DESIGN: {
    key: "NEW_SKILL_CARD_DESIGN",
    name: "New Skill Card Design",
    description: "Updated skill card with improved visual hierarchy",
    type: "redesign",
    defaultEnabled: false,
  },

  // User opt-in features
  BETA_FEATURES: {
    key: "BETA_FEATURES",
    name: "Beta Features",
    description: "Try experimental features before they launch",
    type: "user-opt-in",
    defaultEnabled: false,
  },

  ADVANCED_ANALYTICS: {
    key: "ADVANCED_ANALYTICS",
    name: "Advanced Analytics",
    description: "Detailed insights and reporting",
    type: "user-opt-in",
    defaultEnabled: false,
    requiresPremium: true,
  },

  // Admin-controlled features
  LANDING_PAGE_LIVE: {
    key: "LANDING_PAGE_LIVE",
    name: "Landing Page Live",
    description: "Enable signup and login buttons on marketing pages",
    type: "admin",
    defaultEnabled: true, // Set to true to enable login/signup
    adminOverride: import.meta.env.VITE_LANDING_PAGE_LIVE !== "false",
  },

  MAINTENANCE_MODE: {
    key: "MAINTENANCE_MODE",
    name: "Maintenance Mode",
    description: "Show maintenance banner",
    type: "admin",
    defaultEnabled: import.meta.env.VITE_MAINTENANCE_MODE === "true",
  },

  NEW_FEATURE_ANNOUNCEMENT: {
    key: "NEW_FEATURE_ANNOUNCEMENT",
    name: "Feature Announcement Banner",
    description: "Show announcement for new features",
    type: "admin",
    defaultEnabled: import.meta.env.VITE_SHOW_ANNOUNCEMENT === "true",
  },

  // Gradual rollout features
  ENHANCED_SEARCH: {
    key: "ENHANCED_SEARCH",
    name: "Enhanced Search",
    description: "Improved search with filters and suggestions",
    type: "rollout",
    defaultEnabled: false,
    rolloutPercentage: parseInt(
      import.meta.env.VITE_ENHANCED_SEARCH_ROLLOUT || "0",
    ),
  },

  // A/B test features
  NEW_ONBOARDING_FLOW: {
    key: "NEW_ONBOARDING_FLOW",
    name: "New Onboarding Flow",
    description: "A/B test for improved onboarding",
    type: "ab-test",
    defaultEnabled: false,
  },
} as const;

export type FeatureFlagKey = keyof typeof FEATURE_FLAGS;

// User preferences stored in localStorage and database
interface UserFeaturePreferences {
  [key: string]: boolean;
}

// Feature flag state management
interface FeatureFlagState {
  flags: Record<string, boolean>;
  userPreferences: UserFeaturePreferences;
  isLoading: boolean;
}

interface FeatureFlagContextValue extends FeatureFlagState {
  isEnabled: (flag: FeatureFlagKey) => boolean;
  toggleFeature: (flag: FeatureFlagKey, enabled: boolean) => Promise<void>;
  getUserPreference: (flag: FeatureFlagKey) => boolean | undefined;
  canUserToggle: (flag: FeatureFlagKey) => boolean;
  getAvailableFlags: () => FeatureFlag[];
}

const FeatureFlagContext = createContext<FeatureFlagContextValue | null>(null);

// Hook to use feature flags
export const useFeatureFlags = () => {
  const context = useContext(FeatureFlagContext);
  if (!context) {
    throw new Error("useFeatureFlags must be used within FeatureFlagProvider");
  }
  return context;
};

// Hook for simple feature check
export const useFeatureFlag = (flag: FeatureFlagKey): boolean => {
  const { isEnabled } = useFeatureFlags();
  return isEnabled(flag);
};

// Provider component
export const FeatureFlagProvider = ({
  children,
  userId,
  isPremium = false,
  isAdmin = false,
}: {
  children: ReactNode;
  userId?: string;
  isPremium?: boolean;
  isAdmin?: boolean;
}) => {
  const [state, setState] = useState<FeatureFlagState>({
    flags: {},
    userPreferences: {},
    isLoading: true,
  });

  // Load user preferences from localStorage and database
  useEffect(() => {
    const loadPreferences = async (): Promise<void> => {
      try {
        // Load from localStorage
        const localPrefs = localStorage.getItem("featureFlags");
        const userPreferences = localPrefs ? JSON.parse(localPrefs) : {};

        // TODO: Load from database if user is authenticated
        // const dbPrefs = await client.models.UserFeaturePreference.list({
        //   filter: { userId: { eq: userId } }
        // });

        // Calculate enabled flags
        const flags = calculateEnabledFlags(
          userPreferences,
          userId,
          isPremium,
          isAdmin,
        );

        setState({
          flags,
          userPreferences,
          isLoading: false,
        });
      } catch (error) {
        console.error("Failed to load feature flags:", error);
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    loadPreferences();
  }, [userId, isPremium, isAdmin]);

  // Calculate which flags are enabled
  const calculateEnabledFlags = (
    userPrefs: UserFeaturePreferences,
    userId?: string,
    isPremium?: boolean,
    isAdmin?: boolean,
  ): Record<string, boolean> => {
    const flags: Record<string, boolean> = {};

    Object.entries(FEATURE_FLAGS).forEach(([key, config]) => {
      // Admin override takes precedence
      if (config.adminOverride !== undefined) {
        flags[key] = config.adminOverride;
        return;
      }

      // Check premium requirement
      if (config.requiresPremium && !isPremium && !isAdmin) {
        flags[key] = false;
        return;
      }

      switch (config.type) {
        case "admin":
          // Admin-controlled flags use environment variables
          flags[key] = config.defaultEnabled;
          break;

        case "user-opt-in":
        case "redesign":
          // User can toggle these
          flags[key] = userPrefs[key] ?? config.defaultEnabled;
          break;

        case "rollout":
          // Percentage-based rollout
          if (config.rolloutPercentage !== undefined && userId) {
            const userHash = hashUserId(userId);
            flags[key] = userHash < config.rolloutPercentage;
          } else {
            flags[key] = config.defaultEnabled;
          }
          break;

        case "ab-test":
          // A/B test - consistent assignment per user
          if (userId) {
            const userHash = hashUserId(userId);
            flags[key] = userHash < 50; // 50/50 split
          } else {
            flags[key] = config.defaultEnabled;
          }
          break;

        default:
          flags[key] = config.defaultEnabled;
      }
    });

    return flags;
  };

  // Check if a feature is enabled
  const isEnabled = (flag: FeatureFlagKey): boolean => {
    return state.flags[flag] ?? false;
  };

  // Toggle a feature (for user-controllable flags)
  const toggleFeature = async (
    flag: FeatureFlagKey,
    enabled: boolean,
  ): Promise<void> => {
    if (!canUserToggle(flag)) {
      console.warn(`Feature ${flag} cannot be toggled by user`);
      return;
    }

    const newPreferences = {
      ...state.userPreferences,
      [flag]: enabled,
    };

    // Save to localStorage
    localStorage.setItem("featureFlags", JSON.stringify(newPreferences));

    // TODO: Save to database if authenticated
    // if (userId) {
    //   await client.models.UserFeaturePreference.update({
    //     userId,
    //     flagKey: flag,
    //     enabled,
    //   });
    // }

    // Update state
    setState((prev) => ({
      ...prev,
      userPreferences: newPreferences,
      flags: {
        ...prev.flags,
        [flag]: enabled,
      },
    }));
  };

  // Get user's preference for a flag
  const getUserPreference = (flag: FeatureFlagKey): boolean | undefined => {
    return state.userPreferences[flag];
  };

  // Check if user can toggle this flag
  const canUserToggle = (flag: FeatureFlagKey): boolean => {
    const config = FEATURE_FLAGS[flag];

    // Admin override prevents user toggle
    if (config.adminOverride !== undefined) {
      return false;
    }

    // Only certain types can be toggled by users
    return config.type === "user-opt-in" || config.type === "redesign";
  };

  // Get all flags available to current user
  const getAvailableFlags = (): FeatureFlag[] => {
    return Object.values(FEATURE_FLAGS).filter((config) => {
      // Filter out admin-only flags for non-admins
      if (config.type === "admin" && !isAdmin) {
        return false;
      }

      // Filter out premium flags for non-premium users
      if (config.requiresPremium && !isPremium && !isAdmin) {
        return false;
      }

      // Only show user-controllable flags
      return config.type === "user-opt-in" || config.type === "redesign";
    });
  };

  const value: FeatureFlagContextValue = {
    ...state,
    isEnabled,
    toggleFeature,
    getUserPreference,
    canUserToggle,
    getAvailableFlags,
  };

  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

// Utility: Hash user ID for consistent A/B assignment
const hashUserId = (userId: string): number => {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  return Math.abs(hash) % 100;
};

// Component wrapper for feature-flagged content
export const FeatureGate = ({
  flag,
  fallback = null,
  children,
}: {
  flag: FeatureFlagKey;
  fallback?: ReactNode;
  children: ReactNode;
}) => {
  const { isEnabled } = useFeatureFlags();

  return isEnabled(flag) ? <>{children}</> : <>{fallback}</>;
};

// Component wrapper for redesigns (shows old or new version)
export const RedesignToggle = ({
  flag,
  oldVersion,
  newVersion,
}: {
  flag: FeatureFlagKey;
  oldVersion: ReactNode;
  newVersion: ReactNode;
}) => {
  const { isEnabled } = useFeatureFlags();

  return isEnabled(flag) ? <>{newVersion}</> : <>{oldVersion}</>;
};

// Simple utility function for checking feature flags without context
// Returns false by default if feature flag system is not set up
export const isFeatureEnabled = (flag: FeatureFlagKey): boolean => {
  try {
    // Check environment variable overrides first
    const config = FEATURE_FLAGS[flag];
    if (config?.adminOverride !== undefined) {
      return config.adminOverride;
    }

    // Check localStorage for user preferences
    const localPrefs = localStorage.getItem("featureFlags");
    if (localPrefs) {
      const prefs = JSON.parse(localPrefs);
      if (prefs[flag] !== undefined) {
        return prefs[flag];
      }
    }

    // Return default value
    return config?.defaultEnabled ?? false;
  } catch (error) {
    console.error("Error checking feature flag:", error);
    return false;
  }
};
